import { reportStepsAPI } from '../../services/api';
import { useReportState } from './useReportState';
import { ReportStep } from './types';
import { validateTaskId, getTaskSelection, saveTaskSelection } from '../../utils/taskIdValidator';
import i18n from '../../i18n';

// Генерация client_id для SSE соединения
const generateClientId = () => {
  // Try to get the user ID from the token
  let userId = "anonymous";
  const token = localStorage.getItem('token');
  
  if (token) {
    try {
      // JWT tokens are in the format header.payload.signature
      // We need to decode the payload (second part)
      const payload = token.split('.')[1];
      // Decode the base64 payload
      const decodedPayload = JSON.parse(atob(payload));
      // Get the user ID from the sub claim
      if (decodedPayload.sub) {
        userId = decodedPayload.sub;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }
  
  return `user_${userId}_${Math.random().toString(36).substring(2, 9)}`;
};

export const useAnalytics = () => {
  const {
    state,
    setTaskId,
    setCurrentStep,
    setIsLoading,
    setError,
    setAnalyticsData,
    // These state setters are not used directly in this hook, we use updateAnalyticsState instead
    // setReports,
    // setSelectedReportIndices,
    updateAnalyticsState
  } = useReportState();

  const loadAnalyticsData = async (forcedTaskId?: string): Promise<boolean> => {
    // Используем переданный taskId или получаем из хранилища
    const { taskId: storageTaskId } = getTaskSelection();
    const currentTaskId = forcedTaskId || storageTaskId || state.taskId;
    
    console.log('[ANALYTICS_DEBUG] loadAnalyticsData - начало выполнения с параметрами:', {
      forcedTaskId,
      storageTaskId,
      stateTaskId: state.taskId,
      currentTaskId
    });
    
    if (!currentTaskId) {
      console.error('[ANALYTICS_DEBUG] loadAnalyticsData - taskId не найден ни в параметрах, ни в хранилище, ни в состоянии');
      return false;
    }
    
    // Сохраняем taskId во все хранилища для надежности
    if (currentTaskId) {
      console.log(`[ANALYTICS_DEBUG] loadAnalyticsData - сохраняем taskId во все хранилища: ${currentTaskId}`);
      // Используем saveTaskSelection для сохранения во все хранилища
      saveTaskSelection(currentTaskId);
    }
    
    if (currentTaskId !== state.taskId) {
      console.log('[ANALYTICS_DEBUG] loadAnalyticsData - обновляем taskId из localStorage:', currentTaskId);
      setTaskId(currentTaskId);
    }
    
    console.log('[ANALYTICS_DEBUG] loadAnalyticsData - начало загрузки аналитических данных для задачи:', currentTaskId);
    try {
      setIsLoading(true);
      
      const response = await reportStepsAPI.getTaskAnalytics(currentTaskId);
      console.log('[ANALYTICS_DEBUG] loadAnalyticsData - получен ответ:', response.data);
      console.log('[ANALYTICS_DEBUG] loadAnalyticsData - статус ответа:', response.data.status);
      
      // Добавляем проверку, что данные соответствуют текущему taskId
      if (response.data.task_id && response.data.task_id !== currentTaskId) {
        console.error(`[ANALYTICS_DEBUG] loadAnalyticsData - несоответствие taskId: ожидался ${currentTaskId}, получен ${response.data.task_id}`);
        // Принудительно перезагружаем данные для правильного taskId
        return false;
      }
      
      if (response.data.status === 'found' && response.data.analytics_data) {
        console.log('[ANALYTICS_DEBUG] loadAnalyticsData - аналитические данные найдены в ответе');
        
        // Добавляем taskId в аналитические данные, если его там нет
        if (!response.data.analytics_data.task_id) {
          console.log('[ANALYTICS_DEBUG] loadAnalyticsData - добавляем taskId в аналитические данные:', currentTaskId);
          response.data.analytics_data.task_id = currentTaskId;
        } else {
          console.log('[ANALYTICS_DEBUG] loadAnalyticsData - taskId уже присутствует в аналитических данных:', response.data.analytics_data.task_id);
        }
        
        // Проверяем соответствие taskId в аналитических данных
        if (!validateTaskId(response.data.analytics_data, currentTaskId)) {
          console.error('[ANALYTICS_DEBUG] loadAnalyticsData - несоответствие taskId в аналитических данных');
          return false;
        }
          console.log('[ANALYTICS_DEBUG] loadAnalyticsData - аналитические данные найдены:', response.data.analytics_data);
          
          // Проверяем наличие отчетов в данных
          if (!response.data.analytics_data.reports || !Array.isArray(response.data.analytics_data.reports)) {
            console.log('[ANALYTICS_DEBUG] loadAnalyticsData - отчеты не найдены в данных');
            return false;
          }
          
          console.log('[ANALYTICS_DEBUG] loadAnalyticsData - форматируем отчеты из ответа:', response.data.analytics_data.reports);
          console.log('[ANALYTICS_DEBUG] loadAnalyticsData - количество отчетов:', response.data.analytics_data.reports.length);
          
          // Форматируем отчеты, сохраняя флаг deleted и index из бэкенда
          const reportsWithDeletedStatus = response.data.analytics_data.reports
            .map((report: any, idx: number) => {
              // Логируем информацию о каждом отчете
              console.log(`[ANALYTICS_DEBUG] loadAnalyticsData - обработка отчета #${idx}:`, {
                name: report.name,
                originalStatus: report.status,
                newStatus: report.status || 'pending',
                originalIndex: report.index,
                newIndex: report.index !== undefined ? report.index : idx,
                deleted: report.deleted || false
              });
              
              return {
                name: report.name,
                status: report.status || 'pending',
                index: report.index !== undefined ? report.index : idx, // Используем индекс из бэкенда, если он есть
                sheets: report.sheets || [],
                processing_steps: report.processing_steps || [],
                query_suggestion: report.query_suggestion || '',
                error: report.error || null,
                deleted: report.deleted || false // Используем значение с бэкенда
              };
            });
          
          console.log('[ANALYTICS_DEBUG] loadAnalyticsData - отформатированные отчеты:', reportsWithDeletedStatus);
          
          // Обновляем все состояния атомарно через reducer
          const indices = reportsWithDeletedStatus.map((report: ReportStep) => report.index);
          console.log('[ANALYTICS_DEBUG] loadAnalyticsData - выбранные индексы отчетов:', indices);
          
          updateAnalyticsState({
            analyticsData: response.data.analytics_data,
            reports: reportsWithDeletedStatus,
            selectedReportIndices: indices,
            isLoading: false
          });
          
          console.log('[ANALYTICS_DEBUG] loadAnalyticsData - состояние успешно обновлено, возвращаем true');
          return true;
      } else {
        console.log('[ANALYTICS_DEBUG] loadAnalyticsData - аналитические данные не найдены, статус:', response.data.status);
        return false;
      }
    } catch (err: any) {
      console.error('[ANALYTICS_DEBUG] loadAnalyticsData - ошибка при загрузке аналитических данных:', err);
      console.error('[ANALYTICS_DEBUG] loadAnalyticsData - детали ошибки:', err.response?.data || err.message);
      return false;
    } finally {
      console.log('[ANALYTICS_DEBUG] loadAnalyticsData - завершение загрузки, isLoading = false');
      setIsLoading(false);
    }
  };

  const generateAnalytics = async (clientId?: string) => {
    const { taskId: currentTaskId } = getTaskSelection();
    
    console.log('[ANALYTICS_DEBUG] generateAnalytics - начало выполнения, taskId из localStorage:', currentTaskId);
    
    if (!currentTaskId) {
      console.error('[ANALYTICS_DEBUG] generateAnalytics - taskId не найден в localStorage');
      setError(i18n.t('create:processing.error_task_not_init'));
      return;
    }
    
    // Если не передан client_id, генерируем новый
    if (!clientId) {
      // Генерируем client_id для SSE соединения
      clientId = generateClientId();
      console.log('[ANALYTICS_DEBUG] generateAnalytics - сгенерирован client_id для SSE:', clientId);
    }
    
    try {
      console.log('[ANALYTICS_DEBUG] generateAnalytics - устанавливаем isLoading = true');
      setIsLoading(true);
      setError(null);
      
      if (currentTaskId !== state.taskId) {
        console.log('[ANALYTICS_DEBUG] generateAnalytics - обновляем taskId из localStorage:', currentTaskId);
        setTaskId(currentTaskId);
      }
      
      console.log('[ANALYTICS_DEBUG] generateAnalytics - отправляем запрос на генерацию аналитики для задачи:', currentTaskId);
      const response = await reportStepsAPI.generateAnalytics(currentTaskId, clientId);
      console.log('[ANALYTICS_DEBUG] generateAnalytics - получен ответ:', response.data);
      
      // Проверяем соответствие taskId в ответе
      if (response.data.task_id && response.data.task_id !== currentTaskId) {
        console.error(`[ANALYTICS_DEBUG] generateAnalytics - несоответствие taskId: ожидался ${currentTaskId}, получен ${response.data.task_id}`);
        setError(`Ошибка: несоответствие идентификатора задачи (${response.data.task_id} вместо ${currentTaskId})`);
        return;
      }
      
      if (response && response.data && response.data.analytics_data) {
        console.log('[ANALYTICS_DEBUG] generateAnalytics - получены аналитические данные');
        
        // Добавляем taskId в аналитические данные, если его там нет
        if (!response.data.analytics_data.task_id) {
          console.log('[ANALYTICS_DEBUG] generateAnalytics - добавляем taskId в аналитические данные:', currentTaskId);
          response.data.analytics_data.task_id = currentTaskId;
        } else {
          console.log('[ANALYTICS_DEBUG] generateAnalytics - taskId уже присутствует в аналитических данных:', response.data.analytics_data.task_id);
        }
        
        // Проверяем соответствие taskId в аналитических данных
        if (!validateTaskId(response.data.analytics_data, currentTaskId)) {
          console.error('[ANALYTICS_DEBUG] generateAnalytics - несоответствие taskId в аналитических данных');
          setError(i18n.t('common:error'));
          return;
        }
        console.log('[ANALYTICS_DEBUG] generateAnalytics - получены аналитические данные из ответа API:', response.data.analytics_data);
        console.log('[ANALYTICS_DEBUG] generateAnalytics - статус ответа:', response.data.status);
        setAnalyticsData(response.data.analytics_data);
        
        if (response.data.analytics_data.reports && Array.isArray(response.data.analytics_data.reports)) {
          console.log('[ANALYTICS_DEBUG] generateAnalytics - форматируем отчеты из ответа:', response.data.analytics_data.reports);
          console.log('[ANALYTICS_DEBUG] generateAnalytics - количество отчетов:', response.data.analytics_data.reports.length);
          
          const reportsWithFullData = response.data.analytics_data.reports
            .map((report: any, idx: number) => {
              // Логируем информацию о каждом отчете
              console.log(`[ANALYTICS_DEBUG] generateAnalytics - обработка отчета #${idx}:`, {
                name: report.name,
                originalStatus: report.status,
                newStatus: report.status || 'pending',
                originalIndex: report.index,
                newIndex: report.index !== undefined ? report.index : idx,
                deleted: report.deleted || false
              });
              
              return {
                name: report.name,
                status: report.status || 'pending',
                index: report.index !== undefined ? report.index : idx, // Используем индекс из бэкенда, если он есть
                sheets: report.sheets || [],
                processing_steps: report.processing_steps || [],
                query_suggestion: report.query_suggestion || '',
                error: report.error || null,
                deleted: report.deleted || false // Сохраняем флаг deleted из бэкенда
              };
            });
          
          console.log('[ANALYTICS_DEBUG] generateAnalytics - отформатированные отчеты:', reportsWithFullData);
          
          // Обновляем все состояния атомарно через reducer
          const indices = reportsWithFullData.map((report: ReportStep) => report.index);
          console.log('[ANALYTICS_DEBUG] generateAnalytics - выбранные индексы отчетов:', indices);
          
          updateAnalyticsState({
            analyticsData: response.data.analytics_data,
            reports: reportsWithFullData,
            selectedReportIndices: indices,
            isLoading: false
          });
          
          console.log('[ANALYTICS_DEBUG] generateAnalytics - устанавливаем currentStep = processing');
          setCurrentStep('processing');
        } else {
          console.warn('[ANALYTICS_DEBUG] generateAnalytics - отчеты не найдены в аналитических данных или не являются массивом');
        }
      } else {
        console.warn('[ANALYTICS_DEBUG] generateAnalytics - аналитические данные отсутствуют в ответе');
      }
      
      console.log('[ANALYTICS_DEBUG] generateAnalytics - устанавливаем currentStep = processing (финальный шаг)');
      setCurrentStep('processing');
    } catch (err: any) {
      console.error('[ANALYTICS_DEBUG] generateAnalytics - ошибка при генерации аналитических данных:', err);
      console.error('[ANALYTICS_DEBUG] generateAnalytics - детали ошибки:', err.response?.data || err.message);
      
      // Проверяем, является ли ошибка таймаутом (502 Bad Gateway) или сетевой ошибкой
      const is502Error = err.response?.status === 502;
      const isNetworkError = err.code === 'ECONNRESET' || err.code === 'ECONNABORTED' || !err.response;
      
      if (is502Error || isNetworkError) {
        console.log('[ANALYTICS_DEBUG] generateAnalytics - обнаружена ошибка таймаута или сетевая ошибка, пытаемся загрузить данные из базы');
        console.log('[ANALYTICS_DEBUG] generateAnalytics - тип ошибки:', is502Error ? '502 Bad Gateway' : 'Network Error');
        
        // Retry-механизм: 20 попыток с интервалом 5 секунд
        const MAX_RETRIES = 20;
        const RETRY_INTERVAL = 5000; // 5 секунд
        let retryCount = 0;
        let loadResult = false;
        
        console.log('[ANALYTICS_DEBUG] generateAnalytics - начинаем retry-механизм: максимум ${MAX_RETRIES} попыток с интервалом ${RETRY_INTERVAL}ms');
        
        while (retryCount < MAX_RETRIES && !loadResult) {
          retryCount++;
          console.log(`[ANALYTICS_DEBUG] generateAnalytics - попытка загрузки данных из базы #${retryCount} из ${MAX_RETRIES}`);
          
          // Ждем перед следующей попыткой
          await new Promise(resolve => setTimeout(resolve, RETRY_INTERVAL));
          
          // Пытаемся загрузить данные из базы
          loadResult = await loadAnalyticsData(currentTaskId);
          
          if (loadResult) {
            console.log(`[ANALYTICS_DEBUG] generateAnalytics - данные успешно загружены из базы на попытке #${retryCount}`);
            setCurrentStep('processing');
            return; // Успешно загрузили данные, выходим
          } else {
            console.log(`[ANALYTICS_DEBUG] generateAnalytics - попытка #${retryCount} не удалась, данные еще не готовы`);
          }
        }
        
        // Если все попытки исчерпаны
        if (!loadResult) {
          console.error(`[ANALYTICS_DEBUG] generateAnalytics - не удалось загрузить данные из базы после ${MAX_RETRIES} попыток`);
          setError(`Превышено время ожидания ответа от сервера. Было выполнено ${MAX_RETRIES} попыток загрузки данных. Попробуйте обновить страницу.`);
        }
      } else {
        setError(err.response?.data?.detail || i18n.t('common:error'));
      }
      setCurrentStep('error');
    } finally {
      console.log('[ANALYTICS_DEBUG] generateAnalytics - завершение генерации, isLoading = false');
      setIsLoading(false);
    }
  };

  // Force reload analytics data after a report status change
  const refreshAnalyticsData = async () => {
    console.log('[ANALYTICS_DEBUG] refreshAnalyticsData - принудительное обновление аналитических данных');
    const result = await loadAnalyticsData();
    console.log('[ANALYTICS_DEBUG] refreshAnalyticsData - результат обновления:', result);
    return result;
  };

  return {
    generateAnalytics,
    loadAnalyticsData,
    refreshAnalyticsData,
    analyticsData: state.analyticsData,
    isLoading: state.isLoading,
    error: state.error,
    currentStep: state.currentStep,
    reports: state.reports
  };
};
